import Logger from "@/utility/logger";
import { TODO_CLOSERS, findTodoSpanEnd, findTodoTerminatorEnd, isTodoPlaceholderAt } from "@/utility/todoSpan";

const SENTENCE_END_MARKS = [".", "!", "?"];

interface TodoProbe {
  match: Word.Range;
  scope: Word.Range;
  before: Word.Range;
  afterMarker: Word.Range;
  marks: Word.RangeCollection[];
}

const loadCandidates = (match: Word.Range, collections: Word.RangeCollection[]): Word.Range[] =>
  collections.flatMap((found) => found.items || []).map((mark) => {
    const candidate = match.expandTo(mark);
    candidate.load("text");
    return candidate;
  });

/**
 * Resolves each `TODO:` match to the range from its marker to the end of its
 * sentence, closing quote or bracket included. Word cannot tell which full stop
 * ends the sentence (a dot can sit inside "18.2." or "e.g."), so the end is
 * computed from the paragraph text and matched to the punctuation range whose
 * text is exactly that long. A TODO with no complete sentence runs to the end of
 * its paragraph and never into the next.
 *
 * A match that is not a placeholder under the shared marker rule (glued to a
 * preceding word, as in "autoTODO:", or inside the sentence of an earlier TODO)
 * is dropped, so the result can be shorter than `matches`.
 */
export const resolveTodoSpanRanges = async (
  context: Word.RequestContext,
  matches: Word.Range[]
): Promise<Word.Range[]> => {
  if (matches.length === 0) return [];

  const probes: TodoProbe[] = matches.map((match) => {
    const paragraph = match.paragraphs.getFirst();
    const paragraphEnd = paragraph.getRange(Word.RangeLocation.end);
    const scope = match.expandTo(paragraphEnd);
    scope.load("text");
    const before = paragraph.getRange(Word.RangeLocation.start).expandTo(match.getRange(Word.RangeLocation.start));
    before.load("text");
    const afterMarker = match.getRange(Word.RangeLocation.after).expandTo(paragraphEnd);
    const marks = SENTENCE_END_MARKS.map((mark) => {
      const found = afterMarker.search(mark, { matchWildcards: false });
      found.load("items");
      return found;
    });
    return { match, scope, before, afterMarker, marks };
  });
  await context.sync();

  const placeholders = probes.filter(({ scope, before }) => {
    const beforeText = before.text || "";
    return isTodoPlaceholderAt(beforeText + (scope.text || ""), beforeText.length);
  });

  const resolutions = placeholders.map((probe) => {
    const text = probe.scope.text || "";
    const end = findTodoSpanEnd(text);
    const terminatorEnd = findTodoTerminatorEnd(text);
    const closers =
      end !== null && terminatorEnd !== null && end > terminatorEnd
        ? text
            .slice(terminatorEnd, end)
            .split("")
            .filter((char, position, chars) => TODO_CLOSERS.includes(char) && chars.indexOf(char) === position)
        : [];
    const closerSearches = closers.map((closer) => {
      const found = probe.afterMarker.search(closer, { matchWildcards: false });
      found.load("items");
      return found;
    });
    return { probe, end, terminatorEnd, closerSearches, candidates: loadCandidates(probe.match, probe.marks) };
  });
  await context.sync();

  const closerCandidates = resolutions.map(({ probe, closerSearches }) => loadCandidates(probe.match, closerSearches));
  if (closerCandidates.some((candidates) => candidates.length > 0)) await context.sync();

  return resolutions.map(({ probe, end, terminatorEnd, candidates }, index) => {
    if (terminatorEnd === null || end === null) return probe.scope;
    const withLength = (list: Word.Range[], length: number) => list.find((candidate) => (candidate.text || "").length === length);
    const resolved = (end > terminatorEnd && withLength(closerCandidates[index], end)) || withLength(candidates, terminatorEnd);
    if (!resolved) {
      Logger.warn("wordTodoSpan.ts: TODO sentence end could not be matched to a range; using the paragraph end.");
    }
    return resolved || probe.scope;
  });
};
